import { TenantProfileType } from '@/modules/services/auth';
import { MenuItem } from '@/modules/types/navigation';
import { Role } from '../auth/roles/role.enum';
import { useUserHasRole } from '../auth/useUserHasRoles';
import { Roles } from '../auth/roles/roles.type';
import MyLibraryRoutes from '@/pages/MyLibrary/MyLibraryRoutes';
import CourseLibraryRoutes from '@/pages/CourseLibrary/CourseLibraryRoutes';
import VideoLibraryRoutes from '@/pages/VideoLibrary/VideoLibraryRoutes';
import {
  CourseLibraryIcon,
  MyElearningIcon,
  VideoLibraryIcon,
} from '@/components/Icons';
import {
  SettingsPublishingRoutes,
  SettingsThemesRoutes,
} from '@/pages/Settings/SettingRoutes';
import { SettingsOutlinedIcon } from 'elmo-elements';
import { generatePath, useHistory, useLocation } from 'react-router-dom';
import { useUserHasCapabilities } from '../auth/useUserHasCapabilities';
import { CourseLibraryPermissions } from '../auth/permissions/courseLibraryPermissions.enum';
import { useMemo } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import ContentManagerBaseRoutes, {
  ContentManagerTabs,
} from '@/pages/ContentManager/ContentManagerRoutes';

interface IUseNavigationSubmenuProps {
  tenantData?: TenantProfileType;
}

interface IUseNavigationSubmenuReturn {
  navigationSubmenu: MenuItem[];
}

export const useNavigationSubmenu = ({
  tenantData,
}: IUseNavigationSubmenuProps): IUseNavigationSubmenuReturn => {
  const { pathname: currentPath } = useLocation();
  const { learningElaCourseLibraryAdmin: isElaCourseLibraryEnabled } =
    useFlags();
  const history = useHistory();
  const isElmoAdmin = useUserHasRole([Role.Admin, Role.Super, Role.Migration]);
  const isCompanyAdmin = useUserHasRole([Roles.CAdmin]);
  const canViewVideoLibrary = useUserHasCapabilities([
    CourseLibraryPermissions.VIEW_VIDEO_LIBRARY,
  ]);
  const canViewCourseLibrary = useUserHasCapabilities([
    CourseLibraryPermissions.VIEW_COURSE_LIBRARY,
  ]);

  const canAccessCourseLibrary = tenantData
    ? +tenantData.configMetadata.enableCourseLibrary > 0 &&
      canViewCourseLibrary !== undefined &&
      canViewCourseLibrary
    : false;
  const canAccessVideoLibrary = tenantData
    ? +tenantData.configMetadata.enableVideoLibrary > 0 &&
      canViewVideoLibrary !== undefined &&
      canViewVideoLibrary
    : false;

  const isMyLibrarySelected = [MyLibraryRoutes.path, '/module'].some((pathx) =>
    currentPath.includes(pathx),
  );
  const isCourseLibrarySelected = [CourseLibraryRoutes.path].some((pathx) =>
    currentPath.includes(pathx),
  );
  const isVideoLibrarySelected = [VideoLibraryRoutes.path].some((pathx) =>
    currentPath.includes(pathx),
  );
  const isSettingsSelected = currentPath.startsWith('/settings');
  const isContentManagerSelected = currentPath.startsWith('/content-manager');

  const settingsPath = useMemo(() => {
    if (isSettingsSelected) {
      return currentPath;
    }

    return isElmoAdmin
      ? SettingsPublishingRoutes.path
      : SettingsThemesRoutes.path;
  }, [currentPath, isElmoAdmin, isSettingsSelected]);

  const myLibraryPath = useMemo(() => {
    const isMyLibraryPath =
      currentPath.startsWith('/my-library') ||
      currentPath.startsWith('/module');

    if (isMyLibraryPath) {
      return currentPath;
    }

    return MyLibraryRoutes.path;
  }, [currentPath]);

  const navigationSubmenu = [
    isElmoAdmin &&
      isElaCourseLibraryEnabled && {
        key: 'app.menu.courseBuilder.contentManager',
        title: 'Content Manager',
        handleClick: () =>
          history.push(
            generatePath(ContentManagerBaseRoutes.path, {
              tabSelected: ContentManagerTabs.Categories,
            }),
          ),

        url: generatePath(ContentManagerBaseRoutes.path, {
          tabSelected: ContentManagerTabs.Categories,
        }),
        icon: () =>
          SettingsOutlinedIcon({
            title: 'Content Manager',
            className: isContentManagerSelected ? 'menu-icon-active' : '',
          }),
        description: '',
        children: [],
        submenuUrl: '',
        isSelected: isContentManagerSelected,
      },
    {
      key: 'app.menu.courseBuilder.myLibrary',
      title: 'My Library',
      handleClick: () => history.push(MyLibraryRoutes.path),
      url: myLibraryPath,
      icon: () =>
        MyElearningIcon({
          title: 'My Library',
          className: isMyLibrarySelected ? 'menu-icon-active' : '',
        }),
      description: '',
      children: [],
      submenuUrl: '',
      isSelected: isMyLibrarySelected,
    },
    canAccessCourseLibrary && {
      key: 'app.menu.courseBuilder.courseLibrary',
      title: 'Course Library',
      handleClick: () => history.push(CourseLibraryRoutes.path),
      url: CourseLibraryRoutes.path,
      icon: () =>
        CourseLibraryIcon({
          title: 'Course Library',
          className: isCourseLibrarySelected ? 'menu-icon-active' : '',
        }),
      description: '',
      children: [],
      submenuUrl: '',
      isSelected: isCourseLibrarySelected,
    },
    canAccessVideoLibrary && {
      key: 'app.menu.courseBuilder.videoLibrary',
      title: 'Video Library',
      handleClick: () => history.push(VideoLibraryRoutes.path),
      url: VideoLibraryRoutes.path,
      icon: () =>
        VideoLibraryIcon({
          title: 'Video Library',
          className: isVideoLibrarySelected ? 'menu-icon-active' : '',
        }),
      description: '',
      children: [],
      submenuUrl: '',
      isSelected: isVideoLibrarySelected,
    },
    (isElmoAdmin || isCompanyAdmin) && {
      key: 'app.menu.courseBuilder.settings',
      title: 'Settings',
      handleClick: () =>
        history.push(
          isElmoAdmin
            ? SettingsPublishingRoutes.path
            : SettingsThemesRoutes.path,
        ),

      url: settingsPath,
      icon: () =>
        SettingsOutlinedIcon({
          title: 'Settings',
          className: isSettingsSelected ? 'menu-icon-active' : '',
        }),
      description: '',
      children: [],
      submenuUrl: '',
      isSelected: isSettingsSelected,
    },
  ].filter(Boolean) as MenuItem[];

  return {
    navigationSubmenu,
  };
};
